import React, { useCallback, useMemo } from "react";
import { DateTime } from "luxon";
import { useAppDispatch, useAppSelector } from "@store";
import { DateRangeDefaults, updateSignalsFilters } from "@store/signals";
import { Select } from "@taskpane/components";
import { DateIcon, FilterIcon } from "./icons";
import {
  DateSelectContent,
  DateSelectContentProps,
  FilterSelectContent,
  FilterSelectContentProps,
} from "./components";
import css from "./Filters.module.scss";

const Filters: React.FC = () => {
  const dispatch = useAppDispatch();

  const dateRangeString = useAppSelector((state) => state.signals.filters.dateRangeString);
  const [fromDateString, toDateString] = dateRangeString.split("|");
  const isCustomSelected = dateRangeString.includes("custom") && fromDateString && toDateString;

  const dateOptions = useMemo(() => {
    const currentDate = DateTime.now();
    const currentDayString = currentDate.toFormat("yyyy-MM-dd");
    const currentWeekString = currentDate.startOf("week").toFormat("yyyy-MM-dd");
    const currentMonthString = currentDate.startOf("month").toFormat("yyyy-MM-dd");
    const currentQuarterString = currentDate.startOf("quarter").toFormat("yyyy-MM-dd");
    const currentYearString = currentDate.startOf("year").toFormat("yyyy-MM-dd");

    return [
      { value: DateRangeDefaults.All, label: "Date", dropdownLabel: "All" },
      { value: `${currentDayString}||today`, label: "Today" },
      { value: `${currentWeekString}||week`, label: "This Week" },
      { value: `${currentMonthString}||month`, label: "This Month" },
      { value: `${currentQuarterString}||quarter`, label: "This Quarter" },
      { value: `${currentYearString}||year`, label: "This Year" },
      {
        value: isCustomSelected
          ? `${fromDateString}|${toDateString}|custom`
          : DateRangeDefaults.Custom,
        label: isCustomSelected
          ? `${fromDateString.split("-").reverse().join("/")} - ${toDateString.split("-").reverse().join("/")}`
          : "Custom",
      },
    ];
  }, [isCustomSelected, fromDateString, toDateString]);

  const handleDateRangeChange = useCallback(
    (value: string) => {
      dispatch(updateSignalsFilters({ dateRangeString: value }));
    },
    [dispatch]
  );

  const handleRenderDateContent = useCallback(
    (props: DateSelectContentProps) => <DateSelectContent {...props} />,
    []
  );

  const handleRenderFiltersContent = useCallback(
    (props: FilterSelectContentProps) => <FilterSelectContent {...props} />,
    []
  );

  const attrs = {
    container: {
      className: css.container,
    },
    dateSelect: {
      icon: DateIcon,
      placeholder: "Date",
      dropdownWidth: "calc(100% - 32px)",
      options: dateOptions,
      selectedValue: dateRangeString,
      onChange: handleDateRangeChange,
      renderContent: handleRenderDateContent,
    },
    filterSelect: {
      icon: FilterIcon,
      placeholder: "Filter Type",
      dropdownWidth: "calc(100% - 32px)",
      renderContent: handleRenderFiltersContent,
    },
  };

  return (
    <div {...attrs.container}>
      <Select {...attrs.dateSelect} />
      <Select {...attrs.filterSelect} />
    </div>
  );
};

export default Filters;
